import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { Utils } from 'src/app/utils.class';
import { environment } from 'src/environments/environment';
import { EntitiesListAbstractComponent } from './entities-list-abstract.component';
import { PlanningDetailDialogComponent } from './planning-detail-dialog.component';

const PlanningStatus = environment.status.Planning;

export class PlanningDetailDialogRequiredRules {
    static worknumber = (component: PlanningDetailDialogComponent) => {
        return (
            [
                PlanningStatus.PlanningGepland,
                PlanningStatus.PlanningDefinitief,
                PlanningStatus.PlanningBevestigd,
            ].indexOf(component.statusControl.value) !== -1
        );
    };

    static location = (component: PlanningDetailDialogComponent) => {
        return ![
            PlanningStatus.PlanningReservering,
            PlanningStatus.PlanningSlaapdag,
            PlanningStatus.PlanningGeblokkeerd,
        ].includes(component.statusControl.value);
    };

    static asphalt_location = (component: PlanningDetailDialogComponent) => {
        return (
            ![
                PlanningStatus.PlanningReservering,
                PlanningStatus.PlanningSlaapdag,
                PlanningStatus.PlanningGeblokkeerd,
            ].includes(component.statusControl.value) &&
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam
        );
    };

    static sets = (component: PlanningDetailDialogComponent) => {
        return (
            !AuthorisationService.hasFunctionality(Functionalities.TransportplanningManagement) &&
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            [
                PlanningStatus.PlanningReservering,
                PlanningStatus.PlanningBakvanwessel,
                PlanningStatus.PlanningSlaapdag,
                PlanningStatus.PlanningGeblokkeerd,
            ].indexOf(component.statusControl.value) === -1
        );
    };

    static dumpers = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            [PlanningStatus.PlanningDefinitief, PlanningStatus.PlanningBevestigd].includes(
                component.statusControl.value
            )
        );
    };

    static cranes = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            [PlanningStatus.PlanningDefinitief, PlanningStatus.PlanningBevestigd].includes(
                component.statusControl.value
            )
        );
    };

    static rollers = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            [PlanningStatus.PlanningDefinitief, PlanningStatus.PlanningBevestigd].includes(
                component.statusControl.value
            )
        );
    };

    static date = (component: PlanningDetailDialogComponent) => {
        return component.statusControl.value !== PlanningStatus.PlanningBakvanwessel;
    };

    static begintime = PlanningDetailDialogRequiredRules.date;
    static endtime = PlanningDetailDialogRequiredRules.date;

    static definitief = (component: PlanningDetailDialogComponent) => {
        return [PlanningStatus.PlanningDefinitief, PlanningStatus.PlanningBevestigd].includes(
            component.statusControl.value
        );
    };

    static definitiefTeam = (component: PlanningDetailDialogComponent) => {
        return (
            [PlanningStatus.PlanningDefinitief, PlanningStatus.PlanningBevestigd].includes(
                component.statusControl.value
            ) && component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam
        );
    };

    static traffic_controllers = PlanningDetailDialogRequiredRules.definitiefTeam;
    static traffic = PlanningDetailDialogRequiredRules.definitiefTeam;
    static thickness = PlanningDetailDialogRequiredRules.definitief;
    static trucks = PlanningDetailDialogRequiredRules.definitief;
    static tons = PlanningDetailDialogRequiredRules.definitief;

    static pouring_off = PlanningDetailDialogRequiredRules.definitiefTeam;
    static hotbox = PlanningDetailDialogRequiredRules.definitiefTeam;
    static scatter = PlanningDetailDialogRequiredRules.definitiefTeam;
    static sticky_wagon = PlanningDetailDialogRequiredRules.definitiefTeam;

    static gepland = (component: PlanningDetailDialogComponent) => {
        return (
            [
                PlanningStatus.PlanningDefinitief,
                PlanningStatus.PlanningBevestigd,
                PlanningStatus.PlanningGepland,
            ].indexOf(component.statusControl.value) !== -1
        );
    };

    static performer = PlanningDetailDialogRequiredRules.gepland;

    static size = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            ![PlanningStatus.PlanningBakvanwessel, PlanningStatus.PlanningSlaapdag].includes(
                component.statusControl.value
            )
        );
    };

    static asphalt_performer = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            [
                PlanningStatus.PlanningDefinitief,
                PlanningStatus.PlanningBevestigd,
                PlanningStatus.PlanningGepland,
            ].indexOf(component.statusControl.value) !== -1
        );
    };

    static asfaltteam = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.AsfaltTeam &&
            component.statusControl.value !== PlanningStatus.PlanningBakvanwessel
        );
    };

    static projectteam = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.ProjectTeam &&
            ![
                PlanningStatus.PlanningBakvanwessel,
                PlanningStatus.PlanningSlaapdag,
                PlanningStatus.PlanningGeblokkeerd,
            ].includes(component.statusControl.value)
        );
    };

    static wipetruck = (component: PlanningDetailDialogComponent) => {
        return component.mainPlanning.entitytype_key === EntityTypeCode.Wipetruck;
    };

    static driver_user_id = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entitytype_key === EntityTypeCode.Wipetruck &&
            PlanningDetailDialogRequiredRules.definitief(component)
        );
    };

    static projectmanager = (component: PlanningDetailDialogComponent) => {
        return ![PlanningStatus.PlanningSlaapdag, PlanningStatus.PlanningGeblokkeerd].includes(
            component.statusControl.value
        );
    };

    static contractor = (component: PlanningDetailDialogComponent) => {
        return ![PlanningStatus.PlanningGeblokkeerd].includes(component.statusControl.value);
    };

    static hiring_id = (component: PlanningDetailDialogComponent) => {
        return (
            component.mainPlanning.entity &&
            !component.mainPlanning.entity.use_once &&
            [PlanningStatus.PlanningDefinitief, PlanningStatus.PlanningBevestigd].includes(
                component.planning.status_key
            )
        );
    };

    static resident_letter = (component: PlanningDetailDialogComponent) => {
        return (
            [
                PlanningStatus.PlanningDefinitief,
                PlanningStatus.PlanningBevestigd,
                PlanningStatus.PlanningGepland,
            ].includes(component.statusControl.value) && component.mainPlanning.id > 15470
        );
    };
}

export function disabledWhenFinal(
    component: PlanningDetailDialogComponent | EntitiesListAbstractComponent<any>
): boolean {
    return (
        !!component.planning.id &&
        (component.initialStatus === PlanningStatus.PlanningBevestigd ||
            (component.initialStatus === PlanningStatus.PlanningDefinitief &&
                Utils.getTimeOrNull(component.fixedDate) >= Utils.getTimeOrNull(component.mainPlanning.begindate)))
    );
}

export function RequiredRule(component: PlanningDetailDialogComponent): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const getControlName = (c: AbstractControl): string | null => {
            if (c && c.parent) {
                const formGroup = c.parent.controls;
                return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
            }
        };

        // controlName is empty on initializing because parent has not been set yet
        const controlName = getControlName(control);
        if (controlName) {
            const rule = PlanningDetailDialogRequiredRules[controlName];
            if (!rule) {
                throw (
                    'No rule defined for ' +
                    controlName +
                    ', please define rule in planning-detail-dialog.required-rules.ts'
                );
            }

            if (
                (control.value == null || (Array.isArray(control.value) && control.value.length < 1)) &&
                rule.call(null, component)
            ) {
                return { required: true };
            }
            return null;
        }
        return { required: true };
    };
}
